/**
 * Compare confidence is intentionally simple and stable. It is derived from the
 * gap between the top two ranked photos so web and iOS can explain when a
 * winner is obvious versus when the user should keep testing alternatives.
 */
export function getWinnerConfidence(rankings) {
    const sorted = [...rankings].sort((a, b) => b.score - a.score);
    const top = sorted[0];
    const runnerUp = sorted[1];
    if (!top || !runnerUp) {
        return {
            level: 'medium',
            label: 'Limited comparison',
            detail: 'Add at least two scored photos to judge how strong the winner really is.',
            scoreGap: 0,
        };
    }
    const scoreGap = top.score - runnerUp.score;
    if (scoreGap >= 12) {
        return {
            level: 'high',
            label: 'Clear winner',
            detail: 'The top photo has enough separation to lead confidently without another immediate retest.',
            scoreGap,
        };
    }
    if (scoreGap >= 6) {
        return {
            level: 'medium',
            label: 'Moderate edge',
            detail: 'The winner is ahead, but the next best option is still worth keeping as a backup.',
            scoreGap,
        };
    }
    return {
        level: 'low',
        label: 'Tight call',
        detail: 'The top two photos are close enough that a tighter A/B test or fresh retake could still change the winner.',
        scoreGap,
    };
}
/**
 * Use the existing ranking order to turn raw scores into a user-facing
 * recommendation. Dating gets a full lineup recommendation. LinkedIn gets a
 * shorter primary-headshot shortlist because only one photo is typically used.
 */
export function getRankingSequenceRecommendation(vertical, rankings) {
    const sorted = [...rankings].sort((a, b) => b.score - a.score);
    if (sorted.length < 2) {
        return null;
    }
    if (vertical === 'dating') {
        const items = sorted.slice(0, 5).map((ranking, index) => ({
            photoId: ranking.photoId,
            score: ranking.score,
            position: index + 1,
            title: DATING_SEQUENCE_TITLES[index] ?? `Photo ${index + 1}`,
            rationale: DATING_SEQUENCE_RATIONALES[index] ?? 'Keep this later in the lineup to avoid repeating the same signal.',
        }));
        return {
            title: 'Suggested dating photo order',
            subtitle: 'Lead with your strongest first impression, then keep the lineup moving with support shots that feel distinct.',
            items,
        };
    }
    const items = sorted.slice(0, 3).map((ranking, index) => ({
        photoId: ranking.photoId,
        score: ranking.score,
        position: index + 1,
        title: LINKEDIN_SEQUENCE_TITLES[index] ?? `Option ${index + 1}`,
        rationale: LINKEDIN_SEQUENCE_RATIONALES[index] ?? 'Keep this as a lower-priority fallback for future refreshes or alternate uses.',
    }));
    return {
        title: 'Recommended LinkedIn shortlist',
        subtitle: 'Use the strongest headshot first, then keep the next options as backup picks for future refreshes or secondary surfaces.',
        items,
    };
}
export function getModelAgreementConfidence(scores) {
    const validScores = scores.filter(score => Number.isFinite(score));
    if (validScores.length < 2) {
        return {
            level: 'medium',
            label: 'Limited model agreement',
            detail: 'More than one finished model score gives a clearer read on how stable the result is.',
            scoreGap: 0,
        };
    }
    const sorted = [...validScores].sort((a, b) => b - a);
    const scoreGap = sorted[0] - sorted[sorted.length - 1];
    if (scoreGap <= 5) {
        return {
            level: 'high',
            label: 'High model agreement',
            detail: 'The models are tightly aligned, so this result is directionally reliable.',
            scoreGap,
        };
    }
    if (scoreGap <= 10) {
        return {
            level: 'medium',
            label: 'Moderate model agreement',
            detail: 'The models broadly agree, but another photo or compare run could still sharpen the decision.',
            scoreGap,
        };
    }
    return {
        level: 'low',
        label: 'Mixed model agreement',
        detail: 'The models disagree more than usual. Treat this as directional and compare a few alternatives before locking it in.',
        scoreGap,
    };
}
const DATING_SEQUENCE_TITLES = [
    'Lead photo',
    'Second photo',
    'Third photo',
    'Fourth photo',
    'Closer',
];
const DATING_SEQUENCE_RATIONALES = [
    'Use this first. It creates the strongest opening signal and should carry most of the swipe decision.',
    'Keep this second to reinforce warmth and trust without repeating the exact same look.',
    'Use this third if it adds a different setting, outfit, or energy to the lineup.',
    'Place this later so it adds range rather than competing with the first two photos.',
    'Use this as the final optional slot if you need one more supporting shot.',
];
const LINKEDIN_SEQUENCE_TITLES = [
    'Primary headshot',
    'Backup headshot',
    'Alternate option',
];
const LINKEDIN_SEQUENCE_RATIONALES = [
    'Use this on LinkedIn first. It gives the clearest professional first impression.',
    'Keep this as your backup if you want a slightly different expression, crop, or future refresh.',
    'Use this only for alternate surfaces, team pages, or future retests.',
];
